import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Send, MapPin, Phone, Mail, ArrowRight } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { notifyForm } from '@/lib/notifyForm';

const Footer: React.FC = () => {
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const [emailError, setEmailError] = useState('');
  const navigate = useNavigate();

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      setEmailError('Please enter your email');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailError('Please enter a valid email');
      return;
    }
    const cleanEmail = email.trim().toLowerCase();
    try {
      await supabase.from('newsletter_subscribers').insert({ email: cleanEmail, source: 'footer' });
    } catch (err) {
      // Ignore duplicates / errors; still show success to user
      console.error('Newsletter subscription error', err);
    }
    // Add to CRM + notify journey@zeratravel.com
    notifyForm({
      email: cleanEmail,
      source: 'newsletter',
      subject: 'Zera Travel — New Newsletter Subscriber',
      details: { signup_location: 'footer' },
    });
    setSubscribed(true);
    setEmailError('');
  };



  return (
    <footer className="bg-[#1A3B5C] text-white">
      {/* Newsletter Banner */}
      <div className="border-b border-white/10">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 py-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <h3 className="font-serif text-3xl md:text-4xl text-white font-light mb-3">
                Stay Inspired by Albania
              </h3>
              <p className="font-sans text-sm text-white/60 leading-relaxed">
                Receive curated Albanian travel inspiration, seasonal highlights, and exclusive access to our newest experiences.
              </p>
            </div>
            <div>
              {subscribed ? (
                <div className="flex items-center gap-3 bg-white/5 p-6">
                  <div className="w-10 h-10 rounded-full bg-[#C9A961]/20 flex items-center justify-center flex-shrink-0">
                    <Send className="w-4 h-4 text-[#C9A961]" />
                  </div>
                  <div>
                    <p className="font-sans text-sm text-white">Faleminderit! Welcome to the Zera world.</p>
                    <p className="font-sans text-xs text-white/50">You'll receive our next Albanian travel collection shortly.</p>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-3">
                  <div className="flex-1">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => { setEmail(e.target.value); setEmailError(''); }}
                      placeholder="Your email address"
                      className="w-full bg-white/5 border border-white/15 px-5 py-3.5 font-sans text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#C9A961] transition-colors"
                    />
                    {emailError && <span className="font-sans text-[10px] text-red-300 mt-1 block">{emailError}</span>}
                  </div>
                  <button type="submit" className="font-sans text-[10px] tracking-[0.2em] uppercase px-8 py-3.5 bg-[#C9A961] text-white hover:bg-[#b8944d] transition-colors flex items-center justify-center gap-2 whitespace-nowrap">
                    Subscribe <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12 lg:gap-8">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <div className="mb-6">
              <span className="font-serif text-2xl tracking-[0.3em] font-light text-white">ZERA</span>
              <span className="font-sans text-[9px] tracking-[0.25em] uppercase text-[#C9A961] ml-2">Travel</span>
            </div>
            <p className="font-sans text-xs text-white/50 leading-relaxed mb-6 max-w-xs">
              At Zera, we believe that travel at its finest is not merely about destinations; it is about the profound transformation that occurs when extraordinary places meet extraordinary care.


            </p>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <MapPin className="w-3.5 h-3.5 text-[#C9A961]" />
                <span className="font-sans text-xs text-white/50">Tirana | Sarandë | Shkodër</span>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="w-3.5 h-3.5 text-[#C9A961]" />
                <a href="tel:+15612472536" className="font-sans text-xs text-white/50 hover:text-[#C9A961] transition-colors">+1 561-247-2536</a>
              </div>
              <div className="flex items-center gap-3">
                <Mail className="w-3.5 h-3.5 text-[#C9A961]" />
                <a href="mailto:journey@zeratravel.com" className="font-sans text-xs text-white/50 hover:text-[#C9A961] transition-colors">journey@zeratravel.com</a>
              </div>

            </div>
          </div>

          {/* Destinations Column */}
          <div>
            <h4 className="font-sans text-[10px] tracking-[0.3em] uppercase text-[#C9A961] mb-6">Destinations</h4>
            <ul className="space-y-3">
              {[
                { label: 'Albanian Travel', path: '/albanian-travel' },
                { label: 'Luxury Travel', path: '/luxury-travel' },
                { label: 'Corporate Travel', path: '/corporate-travel' },

              ].map((item) => (
                <li key={item.label}>
                  <button onClick={() => navigate(item.path)} className="font-sans text-xs text-white/50 hover:text-[#C9A961] transition-colors">
                    {item.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Experiences Column */}
          <div>
            <h4 className="font-sans text-[10px] tracking-[0.3em] uppercase text-[#C9A961] mb-6">Experiences</h4>
            <ul className="space-y-3">
              {[
                { label: 'Yacht Charters', path: '/yacht-charters' },
                { label: 'Villa Rentals', path: '/villa-rentals' },
                { label: 'Albanian Riviera', path: '/albanian-travel' },
                { label: 'Heritage Cities', path: '/albanian-travel' },
                { label: 'Albanian Alps', path: '/albanian-travel' },
                { label: 'Culinary Journeys', path: '/luxury-travel' },
              ].map((item) => (
                <li key={item.label}>
                  <button onClick={() => navigate(item.path)} className="font-sans text-xs text-white/50 hover:text-[#C9A961] transition-colors">
                    {item.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Company Column */}
          <div>
            <h4 className="font-sans text-[10px] tracking-[0.3em] uppercase text-[#C9A961] mb-6">Company</h4>
            <ul className="space-y-3">
              {[
                { label: 'About Zera', path: '/', hash: 'about' },
                { label: 'Travel Journal', path: '/blog' },
                { label: 'Plan Your Journey', path: '/booking' },
                { label: 'Sustainability', path: '/', hash: 'about' },
                { label: 'Press & Media', path: '/', hash: 'about' },
              ].map((item) => (
                <li key={item.label}>
                  <button
                    onClick={() => {
                      if (item.hash) {
                        navigate('/');
                        setTimeout(() => {
                          const el = document.getElementById(item.hash!);
                          if (el) el.scrollIntoView({ behavior: 'smooth' });
                        }, 100);
                      } else {
                        navigate(item.path);
                      }
                    }}
                    className="font-sans text-xs text-white/50 hover:text-[#C9A961] transition-colors"
                  >
                    {item.label}
                  </button>
                </li>
              ))}
            </ul>

            {/* Social Links */}
            <div className="mt-8">
              <h4 className="font-sans text-[10px] tracking-[0.3em] uppercase text-[#C9A961] mb-4">Follow Us</h4>
              <div className="flex items-center gap-4">
                {[
                  { label: 'Instagram', svg: <><rect x="2" y="2" width="20" height="20" rx="5" /><circle cx="12" cy="12" r="5" /><circle cx="17.5" cy="6.5" r="1.5" fill="currentColor" stroke="none" /></> },
                  { label: 'Facebook', svg: <path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z" fill="currentColor" stroke="none" /> },
                  { label: 'Twitter', svg: <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" fill="currentColor" stroke="none" /> },
                  { label: 'TikTok', svg: <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 00-.79-.05A6.34 6.34 0 003.15 15.2a6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.34-6.34V8.79a8.18 8.18 0 004.76 1.52V6.86a4.84 4.84 0 01-1-.17z" fill="currentColor" stroke="none" /> },
                ].map((social) => (
                  <a
                    key={social.label}
                    href={`https://${social.label.toLowerCase()}.com`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-9 h-9 border border-white/15 flex items-center justify-center hover:border-[#C9A961] hover:bg-[#C9A961]/10 transition-all"
                    aria-label={social.label}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-white/60">
                      {social.svg}
                    </svg>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="font-sans text-[10px] text-white/30 tracking-wider">
              &copy; {new Date().getFullYear()} Zera Travel Albania. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              {['Privacy Policy', 'Terms of Service', 'Cookie Policy', 'Accessibility'].map((item) => (
                <button key={item} onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="font-sans text-[10px] text-white/30 hover:text-[#C9A961] transition-colors tracking-wider">
                  {item}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
